/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/notificationsPage.scss';

import React, {MouseEventHandler, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AppContext} from '../misc/appContext';
import {AwesomeIcon, getSetting, Loader, ModelResponse} from '@dvrd/dvr-controls';
import Notification from '../../models/notificationModel';
import {BolmateBadge, BolmateCollapsibleSection} from '../controls';
import PushNotificationsCard from '../pushNotifications/pushNotificationsCard';
import {PushChannelsContext} from '../pushNotifications/pushChannelsContext';
import DeviceList, {DeviceListHandle} from '../pushNotifications/deviceList';
import EmailRecipientsList from '../pushNotifications/emailRecipientsList';
import {isPushPromptInFlight, PWA_PUSH_DEVICES_REFRESH_EVENT} from '../../hooks/usePushSubscription';
import ReviewNotifications from './reviewNotifications/reviewNotifications';
import BuyBoxNotifications from './buyBoxNotifications/buyBoxNotifications';
import StockNotifications from './stockNotifications/stockNotifications';
import OrderNotifications from './orderNotification/orderNotifications';
import ProductHijackNotifications from './productHijackNotification/productHijackNotification';
import InfoBanner from '../misc/infoBanner';
import {awaitActiveServiceWorker} from '../../util/pwaUtil';

import {isCurrentDevice} from '../../util/pushChannelUtil';

export interface NotificationTypeProps {
    onChangeNotification: (notification: Notification, onSaved?: ModelResponse<Notification>) => void;
    onReplaceNotification: (notification: Notification) => void;
    onDeleteNotification: (notification: Notification) => MouseEventHandler;
    onSendTestNotification: (notification: Notification) => MouseEventHandler;
    notifications: Array<Notification>;
    loading: boolean;
    moduleActive: boolean;
    canEdit: boolean;
    canDelete: boolean;
}

export interface NotificationProps extends NotificationTypeProps {
    notification: Notification;
    disabled: boolean;
}

interface Props extends NotificationTypeProps {
    onClickAdd: MouseEventHandler;
    onReloadNotifications: () => void;
    firstRunPushEnable: boolean;
    onEnablePushOnAll: (deviceId: string) => void;
}

export default function NotificationsPage(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {
        loading,
        onSendTestNotification,
        notifications,
        moduleActive,
        onChangeNotification,
        onDeleteNotification,
        onClickAdd,
        canDelete,
        canEdit,
        onReplaceNotification,
        onReloadNotifications,
        firstRunPushEnable,
        onEnablePushOnAll
    } = props;
    // --- Phase 5 web push ---
    // All push UI is gated by the `enableWebPush` feature flag in settings.ts.
    // To disable push rapidly (e.g. incident response) flip the flag to
    // `false` and redeploy the frontend — backend endpoints will still
    // exist but no user can subscribe, and existing subscribers simply stop
    // seeing the card.
    const enableWebPush = getSetting('enableWebPush') === true;
    // The push status card + device panel are an ADMIN diagnostics view: only
    // administrators, and admins logged in as a user (isAdmin() covers both via
    // hasAdminLogin), see them. Regular users manage their subscription from
    // each notification's channels select.
    const isAdminViewer = user?.isAdmin() === true;
    const deviceListRef = useRef<DeviceListHandle | null>(null);
    // Provided by the controller (it wraps this page AND the add-notification
    // wizard in one PushChannelsProvider, so both share the same push state).
    const pushChannels = useContext(PushChannelsContext);
    // `null` = still probing, `true` = active SW found, `false` = unsupported
    // or never registered. We show the warning banner only for `false`.
    const [swAvailable, setSwAvailable] = useState<boolean | null>(null);
    const [swBannerDismissed, setSwBannerDismissed] = useState(false);

    // Single source of truth for refreshing push state: the shared provider
    // (subscription state + device tokens feeding every channels select) and
    // the admin device list ALWAYS refresh together, so they can never
    // disagree. Every trigger (channel toggle, manual refresh, device removed,
    // an out-of-page push change event) routes through here.
    const refreshPushAndDevices = useCallback(() => {
        void pushChannels?.refresh();
        deviceListRef.current?.refresh();
    }, [pushChannels]);

    useEffect(() => {
        // Skip the visibility/focus re-sync while a permission prompt is in
        // flight: the OS dialog toggles visibility/focus, and refreshing then
        // competes with the prompt for the main thread (froze the dialog).
        function onVisible() {
            if (document.visibilityState === 'visible' && !isPushPromptInFlight()) refreshPushAndDevices();
        }
        // Switching between the PWA and the browser tab (or returning from
        // settings) re-syncs BOTH at once via visibilitychange. Deliberately NO
        // 'focus' listener: returning to the tab fires focus AND
        // visibilitychange back-to-back, which doubled every refresh (4
        // parallel listDeviceTokens per tab switch). The explicit push-changed
        // event always refreshes (it fires after a state change, not from
        // visibility).
        window.addEventListener(PWA_PUSH_DEVICES_REFRESH_EVENT, refreshPushAndDevices);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener(PWA_PUSH_DEVICES_REFRESH_EVENT, refreshPushAndDevices);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [refreshPushAndDevices]);

    useEffect(() => {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
            setSwAvailable(false);
            return;
        }
        let cancelled = false;
        // awaitActiveServiceWorker is robust on iOS standalone PWAs, where
        // navigator.serviceWorker.ready can hang indefinitely even though a
        // usable registration exists: it checks an already-active registration
        // directly, (re)registers /sw.js if none exists yet and waits for
        // activation via `statechange`, all bounded by a timeout. The previous
        // ready/getRegistration probe wrongly flagged "Service Worker
        // unavailable" on iOS even when the SW was fine.
        awaitActiveServiceWorker(15000)
            .then(() => {
                if (!cancelled) setSwAvailable(true);
            })
            .catch(() => {
                if (!cancelled) setSwAvailable(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // The first-run prompt's "Ja" lands here: it already subscribed this
    // device, and every configured notification must now get the push channel
    // pointed at it. Applied once, as soon as all four inputs are there —
    // notifications loaded, edit rights, this browser's endpoint resolved, and
    // its token row present in the account's device list.
    const firstRunPushAppliedRef = useRef(false);
    const firstRunPushRetriedRef = useRef(false);
    useEffect(() => {
        if (!firstRunPushEnable || firstRunPushAppliedRef.current) return;
        if (loading || !canEdit || notifications.length === 0) return;
        if (!pushChannels || pushChannels.devicesLoading) return;
        // Still reading this browser's subscription from the SW — the state
        // update that resolves it re-runs this effect.
        if (!pushChannels.currentEndpoint) return;
        const currentDevice = pushChannels.devices.find(device =>
            isCurrentDevice(device, pushChannels.currentEndpoint)
        );
        if (!currentDevice) {
            // The provider loads the device list on mount, which can race the
            // registration the prompt just made (the row lands a beat later and
            // nothing else would refetch until a tab switch). Ask once more,
            // then give up rather than loop.
            if (firstRunPushRetriedRef.current) return;
            firstRunPushRetriedRef.current = true;
            void pushChannels.refresh();
            return;
        }
        firstRunPushAppliedRef.current = true;
        onEnablePushOnAll(currentDevice.id);
    }, [firstRunPushEnable, loading, canEdit, notifications, pushChannels, onEnablePushOnAll]);

    function renderServiceWorkerBanner() {
        if (!enableWebPush) return null;
        if (swAvailable !== false) return null;
        if (swBannerDismissed) return null;
        return (
            <InfoBanner variant='warning' persistDismiss={false} onClose={() => setSwBannerDismissed(true)}>
                {t(
                    'notifications.swUnavailable',
                    'Push-meldingen zijn momenteel niet beschikbaar. Herlaad de pagina of gebruik een recente browser.'
                )}
            </InfoBanner>
        );
    }

    function renderAddButton() {
        return (
            <div className='btn-add' onClick={onClickAdd}>
                <div className='plus-circle'>
                    <AwesomeIcon name='plus' className='plus-icon' />
                </div>
                <label className='plus-label'>
                    {t('notifications.list.addEmailNotification', 'Notificatie toevoegen')}
                </label>
            </div>
        );
    }

    return (
        <>
            <InfoBanner
                featureKey='notifications'
                variant='info'
                title={t('notifications.list.title', 'Notificaties')}
                showForNewUsersOnly={true}
            >
                {t(
                    'notifications.list.infoBannerText',
                    'Ontvang notificaties per e-mail bij belangrijke events, zodat je op de hoogte bent en eventueel actie kunt nemen.'
                )}
            </InfoBanner>
            {renderServiceWorkerBanner()}
            <div className='notifications-page'>
                <Loader active={loading} />
                <div className='notifications-header-container'>
                    <label className='notifications-title'>{t('notifications.list.title', 'Notificaties')}</label>
                    <label className='sub-title'>
                        {t(
                            'notifications.list.subtitle',
                            'Ontvang een melding per e-mail of op mobiel bij onderstaande triggers:'
                        )}
                    </label>
                </div>
                {enableWebPush && isAdminViewer && (
                    <BolmateCollapsibleSection
                        title={t('push.title', 'Overzicht push-abonnementen')}
                        icon='mdi:bell-badge-outline'
                        size='big'
                        defaultOpen={false}
                        rightContent={
                            <BolmateBadge
                                color='red'
                                label={t('push.adminOnlyBadge', 'Alleen zichtbaar voor admin!')}
                            />
                        }
                        className='push-section-collapsible'
                    >
                        <div className='push-section'>
                            <PushNotificationsCard onRefreshRequested={() => deviceListRef.current?.refresh()} />
                            {/* Provider-only refresh: DeviceList already reloads its
                                own rows right before firing onDeviceRemoved — routing
                                this through refreshPushAndDevices loaded the device
                                list a second time for every removal. The notifications
                                refetch picks up the backend's WEB_PUSH prune when the
                                last active device was removed. */}
                            <DeviceList
                                ref={deviceListRef}
                                onDeviceRemoved={() => {
                                    void pushChannels?.refresh();
                                    onReloadNotifications();
                                }}
                            />
                            <EmailRecipientsList notifications={notifications} />
                        </div>
                    </BolmateCollapsibleSection>
                )}
                <ReviewNotifications
                    notifications={notifications}
                    loading={loading}
                    moduleActive={moduleActive}
                    canDelete={canDelete}
                    canEdit={canEdit}
                    onChangeNotification={onChangeNotification}
                    onDeleteNotification={onDeleteNotification}
                    onReplaceNotification={onReplaceNotification}
                    onSendTestNotification={onSendTestNotification}
                />
                <BuyBoxNotifications
                    onChangeNotification={onChangeNotification}
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    onReplaceNotification={onReplaceNotification}
                    notifications={notifications}
                    loading={loading}
                    moduleActive={moduleActive}
                    canEdit={canEdit}
                    canDelete={canDelete}
                />
                <StockNotifications
                    onChangeNotification={onChangeNotification}
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    onReplaceNotification={onReplaceNotification}
                    notifications={notifications}
                    loading={loading}
                    moduleActive={moduleActive}
                    canEdit={canEdit}
                    canDelete={canDelete}
                />
                <ProductHijackNotifications
                    onChangeNotification={onChangeNotification}
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    onReplaceNotification={onReplaceNotification}
                    notifications={notifications}
                    loading={loading}
                    moduleActive={moduleActive}
                    canEdit={canEdit}
                    canDelete={canDelete}
                />
                <OrderNotifications
                    onChangeNotification={onChangeNotification}
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    onReplaceNotification={onReplaceNotification}
                    notifications={notifications}
                    loading={loading}
                    moduleActive={moduleActive}
                    canEdit={canEdit}
                    canDelete={canDelete}
                />
                {renderAddButton()}
            </div>
        </>
    );
}
